import { useEffect, useRef } from 'react';
import { FiCopy, FiEdit2, FiSave, FiTrash2 } from 'react-icons/fi';
import { CurrencyValue } from '../ui/CurrencyValue';
import { StatusBadge } from '../ui/StatusBadge';
import { translateApiMessage } from '../../utils/translateApiMessage';
import { AppTooltip } from '../ui/AppTooltip';

const QueueAction = ({ label, onClick, danger = false, children }) => (
  <AppTooltip label={label}><button
    type='button'
    onClick={onClick}
    aria-label={label}
    className={`inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] ${
      danger
        ? 'border-red-500/25 bg-red-500/10 text-[var(--danger)] hover:bg-red-500/20'
        : 'border-[var(--border-subtle)] bg-[var(--surface-panel)] text-[var(--text-secondary)] hover:border-[var(--brand)] hover:text-[var(--brand)]'
    }`}
  >
    {children}
  </button></AppTooltip>
);

export const TransactionBatchQueue = ({
  items,
  summary,
  customers,
  suppliers,
  editingId,
  onEdit,
  onDuplicate,
  onDelete,
  t,
}) => {
  const listRef = useRef(null);
  const previousItemCountRef = useRef(items.length);
  const customerNames = new Map(customers.map((item) => [item._id, `${item.firstName} ${item.lastName}`]));
  const supplierNames = new Map(suppliers.map((item) => [item._id, item.name]));
  const typeTotals = Object.entries(summary.byType).filter(([type]) => type !== 'incomplete');
  const supplierTotals = Object.entries(summary.bySupplier)
    .sort(([, first], [, second]) => second - first)
    .slice(0, 3);
  const displayItems = items.map((item, originalIndex) => ({ item, originalIndex })).reverse();

  useEffect(() => {
    if (items.length > previousItemCountRef.current) listRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
    previousItemCountRef.current = items.length;
  }, [items.length]);

  return (
    <aside className='transaction-batch-queue flex min-h-0 flex-col rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-panel-muted)] p-3 lg:p-4'>
      <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
        <div className='rounded-[var(--radius-md)] bg-[var(--surface-panel)] p-3 shadow-sm'>
          <p className='text-xs text-[var(--text-muted)]'>{t('Transactions')}</p>
          <p className='mt-1 text-lg font-bold'>{summary.count}</p>
        </div>
        <div className='rounded-[var(--radius-md)] bg-[var(--surface-panel)] p-3 shadow-sm'>
          <p className='text-xs text-[var(--text-muted)]'>{t('Grand Total')}</p>
          <CurrencyValue value={summary.total} className='mt-1 block text-lg font-bold' />
        </div>
        <div className='rounded-[var(--radius-md)] bg-[var(--surface-panel)] p-3 shadow-sm'>
          <p className='text-xs text-[var(--text-muted)]'>{t('Valid')}</p>
          <p className='mt-1 text-lg font-bold text-[var(--success)]'>{summary.valid}</p>
        </div>
        <div className='rounded-[var(--radius-md)] bg-[var(--surface-panel)] p-3 shadow-sm'>
          <p className='text-xs text-[var(--text-muted)]'>{t('Needs review')}</p>
          <p className='mt-1 text-lg font-bold text-[var(--danger)]'>{summary.invalid}</p>
        </div>
      </div>

      {items.length > 0 && (
        <div className='mt-3 space-y-2 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-panel)] p-3'>
          <div className='flex flex-wrap gap-2'>
            {typeTotals.map(([type, total]) => (
              <span key={type} className='inline-flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border-subtle)] px-2.5 py-1 text-xs'>
                <span className='font-semibold text-[var(--text-secondary)]'>{t(type)}</span>
                <CurrencyValue value={total} className='font-bold text-[var(--text-primary)]' />
              </span>
            ))}
          </div>
          {supplierTotals.length > 0 && (
            <div>
              <p className='mb-1.5 text-[11px] font-semibold uppercase text-[var(--text-muted)]'>{t('Top suppliers')}</p>
              <div className='space-y-1.5'>
                {supplierTotals.map(([supplierId, total]) => (
                  <div key={supplierId} className='flex items-center justify-between gap-3 text-xs'>
                    <span className='truncate text-[var(--text-secondary)]'>{supplierNames.get(supplierId) || t('Unknown supplier')}</span>
                    <CurrencyValue value={total} className='shrink-0 font-semibold text-[var(--text-primary)]' />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className='mt-3 flex items-center justify-between gap-3'>
        <div>
          <h3 className='text-sm font-bold text-[var(--text-primary)]'>{t('Transaction batch')}</h3>
          <p className='text-xs text-[var(--text-muted)]'>{t('Review every item before saving.')}</p>
        </div>
        <StatusBadge tone={summary.invalid ? 'danger' : 'success'}>
          {summary.invalid ? t('Needs review') : t('Ready')}
        </StatusBadge>
      </div>

      <div ref={listRef} className='transaction-batch-list app-scrollbar mt-3 min-h-[180px] space-y-2 overflow-y-auto'>
        {items.length === 0 ? (
          <div className='flex min-h-[180px] flex-col items-center justify-center rounded-[var(--radius-md)] border border-dashed border-[var(--border-strong)] px-4 text-center text-sm text-[var(--text-muted)]'>
            <FiSave className='mb-2 h-6 w-6' />
            {t('Add the first transaction to the batch.')}
          </div>
        ) : displayItems.map(({ item, originalIndex }, displayIndex) => {
          const validation = summary.validations[originalIndex];
          const needsReview = !validation.valid || Boolean(item.serverError);
          return (
            <article
              key={item.id}
              className={`transaction-batch-item rounded-[var(--radius-md)] border bg-[var(--surface-panel)] p-3 shadow-sm transition ${
                editingId === item.id
                  ? 'border-[var(--brand)] ring-1 ring-[var(--brand)]'
                  : !needsReview ? 'border-[var(--border-subtle)]' : 'border-red-500/45'
              }`}
              style={{ animationDelay: `${Math.min(displayIndex, 8) * 35}ms` }}
            >
              <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
                <div className='min-w-0'>
                  <div className='flex flex-wrap items-center gap-2'>
                    <span className='text-xs font-bold text-[var(--text-muted)]'>#{originalIndex + 1}</span>
                    <StatusBadge tone={item.transactionType === 'invoice' ? 'success' : 'danger'}>
                      {t(item.transactionType || 'Incomplete')}
                    </StatusBadge>
                    {needsReview && <StatusBadge tone='danger'>{t('Needs review')}</StatusBadge>}
                  </div>
                  <p className='mt-2 truncate text-sm font-semibold text-[var(--text-primary)]'>
                    {customerNames.get(item.customerId) || t('Incomplete transaction')}
                  </p>
                  <p className='truncate text-xs text-[var(--text-secondary)]'>
                    {supplierNames.get(item.supplierId) || '-'} · {item.date || '-'}
                  </p>
                  <CurrencyValue value={item.amount} className='mt-2 block text-sm font-bold' />
                  {validation.duplicateOf !== null && (
                    <p className='mt-1 text-xs text-[var(--danger)]'>{t('Duplicates transaction')} #{validation.duplicateOf + 1}</p>
                  )}
                  {item.serverError && <p className='mt-1 text-xs text-[var(--danger)]'>{translateApiMessage(t, item.serverError)}</p>}
                </div>
                <div className='flex shrink-0 justify-end gap-1.5 sm:ml-auto'>
                  <QueueAction label={t('Edit')} onClick={() => onEdit(item)}><FiEdit2 /></QueueAction>
                  <QueueAction label={t('Duplicate')} onClick={() => onDuplicate(item)}><FiCopy /></QueueAction>
                  <QueueAction label={t('Delete')} onClick={() => onDelete(item.id)} danger><FiTrash2 /></QueueAction>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </aside>
  );
};
